import type { Constructor, Container } from '@packages/core/container';

import { StsBackendClient } from '@packages/clients';
import { ANALYTICS_EVENTS, AnalyticsService } from '@packages/core/analytics';
import { getLogger } from '@packages/core/logging';

import { StsBackendOperation } from '../../../sts-backend-operation';

const logger = getLogger('techline.vin');

/**
 * Parameters for validating a VIN.
 */
export type GetVinValidationRequest = {
    /** The VIN (Vehicle Identification Number) to validate. */
    vin: string;
};

/**
 * Validate a VIN (Vehicle Identification Number) by calling the backend API.
 * Returns whether the VIN is valid according to the API response.
 */
export class ValidateVin extends StsBackendOperation {
    /**
     * Register this operation with additional dependency on AnalyticsService.
     *
     * @param container - the container to register in
     */
    static override registerInContainer(container: Container): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- static `this` in an abstract class is not assignable to Constructor without a cast
        container.register(this as any as Constructor, [StsBackendClient, AnalyticsService]);
    }

    /** @param analyticsService - the analytics service for logging events */
    private analyticsService: AnalyticsService;

    /**
     * @param client - the backend API client
     * @param analyticsService - the analytics service for logging events
     */
    constructor(client: StsBackendClient, analyticsService: AnalyticsService) {
        super(client);
        this.analyticsService = analyticsService;
    }

    /**
     * Validate a VIN (Vehicle Identification Number) by calling the backend API.
     * Returns whether the VIN is valid according to the API response.
     *
     * @param params - the VIN to validate
     * @param signal - optional abort signal for request cancellation
     * @returns the validation result from the API response
     */
    async exec({ vin }: GetVinValidationRequest, signal?: AbortSignal): Promise<boolean> {
        try {
            const response = await this.client.validateVin({ vin }, signal);

            if (response?.success) {
                this.analyticsService.logEvent(ANALYTICS_EVENTS.QMR_NQR_VIN_CHECK_SUBMITTED);
                return response.data?.results;
            }

            return false;
        } catch (e) {
            const message = e instanceof Error ? e.message : 'Failed to validate VIN';
            logger.error('Failed to validate the VIN', { error: e });
            throw new Error(message);
        }
    }
}
